import { useQuery, UseQueryOptions } from 'react-query';

import { AxiosError } from 'axios';

import secAxios from '~/configs/api/axios/secAxios';
import { API_URLS } from '~/configs/api/urls';

export const ACCOUNT_CATEGORY = {
  종합계좌: 'INTEGRATED_ACCOUNT',
  주식계좌: 'TRADING_ACCOUNT',
} as const;

export const STATUS_TYPE = {
  자동주문_약정: 'AUTO_ORDER_CONTRACT',
  파생_ETF_약정: 'DERIVATION_ETF_CONTRACT',
  ETN_약정: 'ETN_CONTRACT',
  변동성_ETN_약정: 'VOLATILITY_ETN_CONTRACT',
  레버리지_ETF_ETN_약정: 'LEVERAGE_ETN_ETF_CONTRACT',
  해외_ETF_약정: 'FOREIGN_ETP_CONTRACT',
  종목추천_약정: 'ITEM_RECOMMEND_CONTRACT',
  소수점매매_약정: 'DECIMAL_POINT_TRADING_CONTRACT',
  국내주식_약정: 'DOMESTIC_CONTRACT',
  해외주식_약정: 'OVERSEA_CONTRACT',
  신용융자_약정: 'CREDIT_LOAN_CONTRACT',
  신용대주_약정: 'CREDIT_LEND_CONTRACT',
  선물하기_약정: 'GIFT_CONTRACT',
  증거금률설정_변경내역존재여부: 'HAS_MARGIN_CHANGE_HISTORY',
  투자성향분석_투자추천여부: 'INVEST_TENDENCY_RECOMMEND',
  투자성향분석등급: 'INVEST_TENDENCY_GRADE',
  미수거래_신청: 'DEFERRED_TRADING',
};

export const STATUS_CODE = {
  YES: 'Y',
  NO: 'N',
  DEFERRED_YES: '00', // 종목별증거금 (미수거래신청)
  DEFERRED_NO: '10', // 증거금 100% (미수거래미신청)
};

export type ACCOUNT_CATEGORY =
  typeof ACCOUNT_CATEGORY[keyof typeof ACCOUNT_CATEGORY];
export type STATUS_TYPE = typeof STATUS_TYPE[keyof typeof STATUS_TYPE];
export type STATUS_CODE = typeof STATUS_CODE[keyof typeof STATUS_CODE];

export type AccountInfo = {
  accountNumber: string; // 계좌 번호
  accountKey: string; // 계좌 식별자
  nickName: string; // 계좌 별칭
  isIntegratedAccount: boolean; // 종합계좌 여부
  isMain: boolean; // 주거래 계좌 (2.0) 여부
  hasPassword: boolean; // 비밀번호 설정 여부
  category: ACCOUNT_CATEGORY; // 상품 코드
  statusList: { type: STATUS_TYPE; code: string }[]; // 계좌 상태 리스트
};

export type AccountInfoResponse = {
  accountList: AccountInfo[];
  memberStatusList: { type: STATUS_TYPE; code: string }[]; // 계정 상태 리스트
  isAvailableOpenAdditionalAccount: boolean; // 추가 근거계좌 개설 가능 여부
};

export const myAccountInfoQueryKey = API_URLS.MEMBER.MY_ACCOUNT;

/**
 * 계정 > 계정 정보 조회
 */
const useMyAccountInfoQuery = (
  options?: UseQueryOptions<AccountInfoResponse, AxiosError<ErrorResponse>>
) => {
  return useQuery<AccountInfoResponse, AxiosError<ErrorResponse>>(
    myAccountInfoQueryKey,
    ({ signal }) =>
      secAxios
        .get(API_URLS.MEMBER.MY_ACCOUNT, { signal })
        .then(({ data }) => data),
    {
      ...options,
      retry: (retryFailureCount, error) => {
        if (retryFailureCount >= 3) {
          console.error(
            `'myAccountInfoQuery' API 실패 횟수 : ${retryFailureCount + 1}`
          );
          // TODO: Matrix 발송
          return false;
        }
        return true;
      },
      retryDelay: 3000,
    }
  );
};

export default useMyAccountInfoQuery;
